import UserPhoneCredentials from 'dashboard/api/userPhoneCredentials';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';

// Break statuses every account has. AWAY / DO_NOT_DISTURB / BUSINESS_TRIP are
// intentionally missing: they are display-only (set externally by the telephony
// system) and still render with the correct colour + label, but must not be
// offered as something an agent can switch into.
export const BUILTIN_BREAK_STATUSES = ['BREAK', 'LUNCH'];

export const getBreakStatuses = (customStatuses = []) => [
  ...BUILTIN_BREAK_STATUSES,
  ...customStatuses,
];

export const isAllowedQueueStatus = (status, customStatuses = []) =>
  status === 'READY' ||
  status === 'NOT_READY' ||
  getBreakStatuses(customStatuses).includes(status);

// The statuses offered next to the main button: everything the agent may switch
// into, minus the one they are already in and the one the main button applies.
export const buildQueueStatusOptions = ({
  currentStatus = null,
  isOnBreak = false,
  customStatuses = [],
}) => {
  const mainButtonAction = isOnBreak ? 'READY' : 'NOT_READY';
  return ['READY', 'NOT_READY', ...getBreakStatuses(customStatuses)].filter(
    status => status !== currentStatus && status !== mainButtonAction
  );
};

// --- Last requested action, per queue ---------------------------------------
// Remembered so `phone/replayLastQueueAction` can restore what the operator
// asked for after a reconnect (the telephony system forgets it on re-register).

const storageKey = accountId =>
  SESSION_STORAGE_KEYS.USER_REQUESTED_PHONE_QUEUE_ACTION(accountId);

export const readQueueActions = accountId => {
  const raw = sessionStorage.getItem(storageKey(accountId));
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
};

export const writeQueueActions = (accountId, map) => {
  sessionStorage.setItem(storageKey(accountId), JSON.stringify(map));
};

export const persistOneQueueAction = (accountId, queueNumber, status) => {
  const map = readQueueActions(accountId);
  map[queueNumber] = status;
  writeQueueActions(accountId, map);
};

export const persistAllQueuesAction = (accountId, queues, status) => {
  const map = {};
  (queues || []).forEach(queue => {
    map[queue.number] = status;
  });
  writeQueueActions(accountId, map);
};

// --- Dispatching -------------------------------------------------------------

// Sends the status change to the telephony system. `queueNumber` null means
// every queue the agent is signed into.
export const dispatchQueueStatus = ({
  agentId,
  queueNumber = null,
  status,
}) => {
  if (queueNumber === null || queueNumber === undefined) {
    if (status === 'READY') return UserPhoneCredentials.ready(agentId);
    if (status === 'NOT_READY') return UserPhoneCredentials.notReady(agentId);
    return UserPhoneCredentials.setQueueStatus(agentId, status);
  }
  if (status === 'READY')
    return UserPhoneCredentials.readyInQueue(agentId, queueNumber);
  if (status === 'NOT_READY')
    return UserPhoneCredentials.notReadyInQueue(agentId, queueNumber);
  return UserPhoneCredentials.setQueueStatusInQueue(
    agentId,
    queueNumber,
    status
  );
};

// What the UI calls: remembers the request, then dispatches it. Anything that
// isn't a status the agent may set falls back to NOT_READY.
export const applyQueueStatus = ({
  accountId,
  agentId,
  queueNumber = null,
  queues = [],
  status,
  customStatuses = [],
}) => {
  const safeStatus = isAllowedQueueStatus(status, customStatuses)
    ? status
    : 'NOT_READY';
  if (queueNumber === null || queueNumber === undefined) {
    persistAllQueuesAction(accountId, queues, safeStatus);
  } else {
    persistOneQueueAction(accountId, queueNumber, safeStatus);
  }
  return dispatchQueueStatus({ agentId, queueNumber, status: safeStatus });
};
